import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUserPreferencesSync } from '@/hooks/use-user-preferences-sync';

export type SidebarPosition = 'left' | 'right';
export type SidebarStyle = 'default' | 'evo';
export type DisplayDensity = 'comfortable' | 'compact';
export type ThemePreference = 'light' | 'dark' | 'system';
export type ProjectCreationWorkflow = 'killer' | 'legacy';

interface UserPreferences {
  sidebarPosition: SidebarPosition;
  sidebarStyle: SidebarStyle;
  showWelcomeDialog: boolean;
  defaultLandingPage: string;
  rememberLastPage: boolean;
  lastVisitedPage?: string;
  displayDensity: DisplayDensity;
  keyboardShortcutsEnabled: boolean;
  theme: ThemePreference;
  projectCreationWorkflow: ProjectCreationWorkflow;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (newPreferences: Partial<UserPreferences>) => void;
  isLoading: boolean;
}

export const defaultPreferences: UserPreferences = {
  sidebarPosition: 'left',
  sidebarStyle: 'evo',
  showWelcomeDialog: true,
  defaultLandingPage: 'dashboard',
  rememberLastPage: false,
  displayDensity: 'comfortable',
  keyboardShortcutsEnabled: true,
  theme: 'light', // Always default to light mode for Evo workspace embedding
  projectCreationWorkflow: 'killer', // New "Killer UX" workflow is the default
};

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const STORAGE_KEY = 'user-preferences';

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);
  const { loadPreferences, savePreferences } = useUserPreferencesSync();

  // Load preferences on mount - try backend first, fallback to localStorage
  useEffect(() => {
    const initializePreferences = async () => {
      try {
        // Try to load from backend
        const backendPrefs = await loadPreferences();
        
        if (backendPrefs) {
          // Map backend preferences to frontend format
          const mappedPrefs: UserPreferences = {
            sidebarPosition: (backendPrefs.sidebarPosition as SidebarPosition) || defaultPreferences.sidebarPosition,
            sidebarStyle: (backendPrefs.sidebarStyle as SidebarStyle) || defaultPreferences.sidebarStyle,
            showWelcomeDialog: backendPrefs.showWelcomeDialog ?? defaultPreferences.showWelcomeDialog,
            defaultLandingPage: backendPrefs.defaultLandingPage || defaultPreferences.defaultLandingPage,
            rememberLastPage: backendPrefs.rememberLastPage ?? defaultPreferences.rememberLastPage,
            lastVisitedPage: backendPrefs.lastVisitedPage,
            displayDensity: (backendPrefs.displayDensity as DisplayDensity) || defaultPreferences.displayDensity,
            keyboardShortcutsEnabled: backendPrefs.keyboardShortcutsEnabled ?? defaultPreferences.keyboardShortcutsEnabled,
            theme: (backendPrefs.theme as ThemePreference) || defaultPreferences.theme,
            projectCreationWorkflow: (backendPrefs.projectCreationWorkflow as ProjectCreationWorkflow) || defaultPreferences.projectCreationWorkflow,
          };
          
          setPreferences(mappedPrefs);
          // Also save to localStorage as a cache
          localStorage.setItem(STORAGE_KEY, JSON.stringify(mappedPrefs));
        } else {
          // Fallback to localStorage if backend fails
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            setPreferences({ ...defaultPreferences, ...JSON.parse(stored) });
          }
        }
      } catch (error) {
        console.error('Failed to initialize user preferences:', error);
        // Fallback to localStorage
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            setPreferences({ ...defaultPreferences, ...JSON.parse(stored) });
          }
        } catch (localError) {
          console.error('Failed to load from localStorage:', localError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializePreferences();
  }, []);

  const updatePreferences = useCallback((newPreferences: Partial<UserPreferences>) => {
    setPreferences((prev) => {
      const updated = { ...prev, ...newPreferences };
      
      // Save to localStorage immediately
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save user preferences to localStorage:', error);
      }
      
      // Save to backend asynchronously (don't block UI)
      savePreferences({
        sidebarPosition: updated.sidebarPosition,
        sidebarStyle: updated.sidebarStyle,
        showWelcomeDialog: updated.showWelcomeDialog,
        defaultLandingPage: updated.defaultLandingPage,
        rememberLastPage: updated.rememberLastPage,
        lastVisitedPage: updated.lastVisitedPage,
        displayDensity: updated.displayDensity,
        keyboardShortcutsEnabled: updated.keyboardShortcutsEnabled,
        theme: updated.theme,
        projectCreationWorkflow: updated.projectCreationWorkflow,
        sensitiveDataVisible: true, // Default value
      }).catch(error => {
        console.error('Failed to save user preferences to backend:', error);
      });
      
      return updated;
    });
  }, [savePreferences]);

  return (
    <UserPreferencesContext.Provider value={{ preferences, updatePreferences, isLoading }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
